import React, { useState } from 'react';
import api from '../../../utils/api';

const BookTypeSelector = ({
  selectedBookType,
  bookOptions,
  onBookTypeSelect,
  onToggleBookDetails,
  onToggleNavigation,
  showBookDetails,
  showNavigation
}) => {
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [customizationData, setCustomizationData] = useState({
    date_of_birth: '',
    category_preferences: [],
    learning_goals: '',
    motivation_style: '',
    country_of_origin: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleCustomizedBookClick = () => {
    if (bookOptions.customized) {
      // If customized book is available, open it
      window.open('/customized-book', '_blank');
    } else {
      // If no customization data, show the modal
      setShowCustomizationModal(true);
    }
  };

  const handleCustomizationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('app_token');
      const response = await api.post('/api/users/book-customization', {
        date_of_birth: customizationData.date_of_birth,
        category_preferences: customizationData.category_preferences,
        learning_goals: customizationData.learning_goals,
        motivation_style: customizationData.motivation_style,
        country_of_origin: customizationData.country_of_origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Close modal and refresh the page to update book options
      setShowCustomizationModal(false);
      window.location.reload();
    } catch (err) {
      console.error('Error updating customization data:', err);
      setError(err.response?.data?.error || 'Failed to save customization data');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (category) => {
    setCustomizationData(prev => ({
      ...prev,
      category_preferences: prev.category_preferences.includes(category)
        ? prev.category_preferences.filter(c => c !== category)
        : [...prev.category_preferences, category]
    }));
  };

  return (
    <>
      <div className="book-type-selector">
        <div className="toggle-buttons">
          <button
            onClick={onToggleBookDetails}
            className={`toggle-button ${showBookDetails ? 'active' : ''}`}
            aria-pressed={showBookDetails}
          >
            {showBookDetails ? 'Hide Book Details' : 'Show Book Details'}
          </button>
          <button
            onClick={onToggleNavigation}
            className={`toggle-button ${showNavigation ? 'active' : ''}`}
            aria-pressed={showNavigation}
            style={{ backgroundColor: '#16a085', color: '#fff', borderColor: '#16a085' }}
          >
            {showNavigation ? 'Hide Navigation' : 'Show Navigation'}
          </button>
        </div>

        {selectedBookType === 'customized' && (
          <div style={{ marginTop: 12 }}>
            <button
              onClick={() => onBookTypeSelect('original')}
              className="btn-primary"
              style={{ backgroundColor: '#16a085' }}
            >
              Back to Original Book
            </button>
          </div>
        )}
      </div>

      {/* Customization Modal */}
      {showCustomizationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20
            }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Personalize Your Book</h2>
              <button
                onClick={() => setShowCustomizationModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCustomizationSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Date of Birth *
                </label>
                <input
                  type="date"
                  required
                  value={customizationData.date_of_birth}
                  onChange={(e) => setCustomizationData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: 8,
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Learning Goals *
                </label>
                <textarea
                  required
                  value={customizationData.learning_goals}
                  onChange={(e) => setCustomizationData(prev => ({ ...prev, learning_goals: e.target.value }))}
                  placeholder="What do you want to learn from this book?"
                  style={{
                    width: '100%',
                    padding: 8,
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14,
                    minHeight: 80,
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Motivation Style *
                </label>
                <textarea
                  required
                  value={customizationData.motivation_style}
                  onChange={(e) => setCustomizationData(prev => ({ ...prev, motivation_style: e.target.value }))}
                  placeholder="How do you stay motivated? What drives you?"
                  style={{
                    width: '100%',
                    padding: 8,
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14,
                    minHeight: 80,
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Country of Origin *
                </label>
                <input
                  type="text"
                  required
                  value={customizationData.country_of_origin}
                  onChange={(e) => setCustomizationData(prev => ({ ...prev, country_of_origin: e.target.value }))}
                  placeholder="Where are you from?"
                  style={{
                    width: '100%',
                    padding: 8,
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Category Preferences *
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Health', 'Productivity', 'Finance', 'Learning', 'Fitness', 'Mindfulness', 'Career', 'Relationships'].map(category => (
                    <label key={category} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 20,
                      cursor: 'pointer',
                      backgroundColor: customizationData.category_preferences.includes(category) ? '#007bff' : 'white',
                      color: customizationData.category_preferences.includes(category) ? 'white' : '#333'
                    }}>
                      <input
                        type="checkbox"
                        checked={customizationData.category_preferences.includes(category)}
                        onChange={() => handleCategoryChange(category)}
                        style={{ marginRight: 6 }}
                      />
                      {category}
                    </label>
                  ))}
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#f8d7da',
                  color: '#721c24',
                  padding: 12,
                  borderRadius: 4,
                  marginBottom: 16,
                  fontSize: 14
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowCustomizationModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: 6,
                    background: '#007bff',
                    color: 'white',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Save & Generate Customized Book'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BookTypeSelector; 